import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './SearchableSelect.css';

const DEBOUNCE_MS = 300;
const MIN_PANEL_HEIGHT = 160;
const MAX_PANEL_HEIGHT = 280;

let uid = 0;
const nextId = () => `ssel-${++uid}`;

const normalizeOptions = (options) =>
  (options || []).map((o) => {
    if (o === null || o === undefined) return { value: '', label: '' };
    if (typeof o === 'string' || typeof o === 'number') return { value: String(o), label: String(o) };
    return { ...o, value: o.value === null || o.value === undefined ? '' : String(o.value) };
  });

/**
 * One reusable searchable dropdown for the whole app — a drop-in
 * replacement for a native <select>.
 *
 * Drop-in contract: onChange is always called with a synthetic
 * event-shaped object (`{ target: { name, value } }`), exactly like a real
 * <select onChange>. Every existing handler in this codebase already reads
 * `e.target.name`/`e.target.value`, so swapping `<select ...>` for
 * `<SearchableSelect ...>` requires no changes to surrounding state/payload
 * logic — only the JSX tag changes.
 *
 * Two modes:
 *  - Local (default): `options` is the full list; typing filters it
 *    client-side, case-insensitively, instantly.
 *  - Remote: pass `onSearchChange(term)` — the component debounces keystrokes
 *    (300ms) and calls it for you; the parent owns fetching and re-passes
 *    the results via `options`/`loading`. Used for large/server-scoped
 *    lists (e.g. members search restricted to the staff member's library).
 *
 * Renders its open panel via a portal at <body> level with `position: fixed`
 * so it always floats above any modal overlay in this app (max observed
 * modal z-index is 100000) and never causes horizontal overflow on mobile —
 * its width always matches the trigger control's own width.
 *
 * Focusing the trigger (Tab, or a click, which focuses before it clicks)
 * opens the dropdown immediately — no typing required to see options.
 *
 * `renderOption(option)` — optional, renders a richer row per option in the
 * open panel (e.g. multiple fields/badges) instead of the plain `label`
 * text; the closed control still always shows the plain `label`, so keep
 * `label` itself reasonably short.
 */
const SearchableSelect = ({
  name,
  id,
  value = '',
  options = [],
  onChange,
  onSearchChange,
  loading = false,
  loadingMessage = 'Loading...',
  placeholder = 'Select...',
  noOptionsMessage = 'No results found',
  searchPlaceholder = 'Search...',
  disabled = false,
  required = false,
  clearable = true,
  className = '',
  panelClassName = '',
  renderOption,
  'aria-label': ariaLabel,
  autoFocus = false
}) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [panelStyle, setPanelStyle] = useState(null);

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const debounceRef = useRef(null);
  const domIdRef = useRef(id || nextId());
  // Selecting an option / clearing / Escape all re-focus the trigger button
  // so keyboard users land back on it — but a native focus event would
  // otherwise immediately re-open the dropdown we just closed. Set right
  // before any programmatic .focus() call; the onFocus handler consumes and
  // clears it once, so only that one refocus is suppressed.
  const suppressFocusOpenRef = useRef(false);

  const isRemote = typeof onSearchChange === 'function';

  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);

  const selectedOption = useMemo(() => {
    const val = value === null || value === undefined ? '' : String(value);
    return normalizedOptions.find((o) => o.value === val);
  }, [normalizedOptions, value]);

  const filteredOptions = useMemo(() => {
    if (isRemote) return normalizedOptions;
    const needle = term.trim().toLowerCase();
    if (!needle) return normalizedOptions;
    return normalizedOptions.filter((o) => o.label.toLowerCase().includes(needle));
  }, [normalizedOptions, term, isRemote]);

  // Remote mode: debounce keystrokes 300ms before asking the parent to
  // re-fetch — large/server-loaded lists never search on every keystroke.
  useEffect(() => {
    if (!isRemote || !open) return undefined;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearchChange(term), DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, isRemote, open]);

  const positionPanel = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < MIN_PANEL_HEIGHT + 48 && spaceAbove > spaceBelow;

    const left = Math.max(4, Math.min(rect.left, viewportW - rect.width - 4));
    const available = (openUpward ? spaceAbove : spaceBelow) - 12;

    setPanelStyle({
      left,
      width: rect.width,
      maxHeight: Math.max(MIN_PANEL_HEIGHT, Math.min(MAX_PANEL_HEIGHT, available)),
      ...(openUpward ? { bottom: viewportH - rect.top + 6 } : { top: rect.bottom + 6 })
    });
  }, []);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setTerm('');
    setHighlightedIndex(-1);
  }, []);

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
    setTerm('');
    setHighlightedIndex(
      normalizedOptions.findIndex((o) => o.value === (value === null || value === undefined ? '' : String(value)))
    );
    // Computed synchronously (not deferred to the next frame) — it only
    // reads the trigger's own already-mounted rect, so the panel is
    // correctly positioned on its very first render instead of needing a
    // fallback hidden style that would otherwise exclude it from the
    // accessibility tree for a frame.
    positionPanel();
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleReposition = () => positionPanel();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    const handleOutsideDown = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      closeDropdown();
    };
    document.addEventListener('mousedown', handleOutsideDown);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      document.removeEventListener('mousedown', handleOutsideDown);
    };
  }, [open, positionPanel, closeDropdown]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open || highlightedIndex < 0 || !panelRef.current) return;
    const node = panelRef.current.querySelectorAll('[data-ssel-option]')[highlightedIndex];
    // Optional chaining on the method itself, not just the node — jsdom
    // (the test environment) doesn't implement scrollIntoView at all.
    node?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedIndex, open]);

  const emitChange = (nextValue) => {
    const event = { target: { name, value: nextValue }, currentTarget: { name, value: nextValue } };
    onChange?.(event);
  };

  const focusTriggerWithoutReopening = () => {
    suppressFocusOpenRef.current = true;
    triggerRef.current?.focus();
  };

  const selectOption = (option) => {
    if (!option || option.disabled) return;
    emitChange(option.value);
    closeDropdown();
    focusTriggerWithoutReopening();
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    emitChange('');
    closeDropdown();
  };

  // Focus alone now opens the dropdown (see handleTriggerFocus), and a click
  // always focuses the button first — so a click on an already-open control
  // must NOT toggle it closed again (that would immediately re-close what
  // the focus event just opened). Closing is handled by Escape, selecting an
  // option, or clicking outside; a click on the trigger just ensures it's
  // open.
  const handleTriggerClick = () => {
    if (!open) openDropdown();
  };

  const handleTriggerFocus = () => {
    if (suppressFocusOpenRef.current) {
      suppressFocusOpenRef.current = false;
      return;
    }
    if (disabled || open) return;
    openDropdown();
  };

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) openDropdown();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = filteredOptions[highlightedIndex >= 0 ? highlightedIndex : 0];
      if (option) selectOption(option);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
      focusTriggerWithoutReopening();
    } else if (e.key === 'Tab') {
      closeDropdown();
    }
  };

  const hasValue = value !== '' && value !== null && value !== undefined;
  const showClear = clearable && !disabled && hasValue;

  return (
    <div className={`ssel ${disabled ? 'ssel--disabled' : ''} ${className}`} ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        id={domIdRef.current}
        className={`ssel__control ${open ? 'ssel__control--open' : ''} ${required && !hasValue ? 'ssel__control--required' : ''}`}
        onClick={handleTriggerClick}
        onFocus={handleTriggerFocus}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={`ssel__value ${!selectedOption ? 'ssel__value--placeholder' : ''}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="ssel__icons">
          {showClear && (
            <span
              role="button"
              tabIndex={-1}
              className="ssel__clear"
              onMouseDown={handleClear}
              aria-label="Clear selection"
              title="Clear"
            >
              <i className="fas fa-times" />
            </span>
          )}
          <i className={`fas fa-chevron-down ssel__chevron ${open ? 'ssel__chevron--open' : ''}`} aria-hidden="true" />
        </span>
      </button>

      {open && !disabled &&
        createPortal(
          <div
            className={`ssel__panel ${panelClassName}`}
            style={panelStyle || { position: 'fixed', visibility: 'hidden' }}
            ref={panelRef}
          >
            <div className="ssel__search-row">
              <i className="fas fa-search ssel__search-icon" aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                className="ssel__search-input"
                placeholder={searchPlaceholder}
                value={term}
                onChange={(e) => {
                  setTerm(e.target.value);
                  setHighlightedIndex(0);
                }}
                onKeyDown={handleSearchKeyDown}
                role="combobox"
                aria-expanded="true"
                aria-controls={`${domIdRef.current}-listbox`}
                aria-autocomplete="list"
              />
            </div>

            <div className="ssel__options" role="listbox" id={`${domIdRef.current}-listbox`}>
              {loading ? (
                <div className="ssel__empty">{loadingMessage}</div>
              ) : filteredOptions.length === 0 ? (
                <div className="ssel__empty">{noOptionsMessage}</div>
              ) : (
                filteredOptions.map((o, index) => (
                  <div
                    key={`${o.value}-${index}`}
                    data-ssel-option
                    role="option"
                    aria-selected={o.value === (value === null || value === undefined ? '' : String(value))}
                    className={[
                      'ssel__option',
                      index === highlightedIndex ? 'ssel__option--highlighted' : '',
                      o.value === (value === null || value === undefined ? '' : String(value)) ? 'ssel__option--selected' : '',
                      o.disabled ? 'ssel__option--disabled' : ''
                    ].filter(Boolean).join(' ')}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectOption(o);
                    }}
                  >
                    {renderOption ? renderOption(o) : o.label}
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SearchableSelect;
